/* eslint-disable rxjs/no-implicit-any-catch */
import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { Observable, throwError, combineLatest, timer, of } from 'rxjs';
import { map, catchError, switchMap, first, repeatWhen, tap, takeWhile } from 'rxjs/operators';

import { SentryLogger, ConfigService } from '@fcom/core';
import { loginToken, profile } from '@fcom/core/selectors';
import { Profile } from '@fcom/core-api/login';

import { Trip, AddFrequentFlyerResponse, ShortTrip } from '../interfaces/pax-trip.interface';
import { TripsActions } from '../store/actions';
import { State } from '../interfaces/store.interface';

@Injectable()
export class PaxTripService {
  private accessToken$: Observable<string>;
  private profile$: Observable<Profile>;

  constructor(
    private http: HttpClient,
    private sentryLogger: SentryLogger,
    private store$: Store<State>,
    private configService: ConfigService
  ) {
    this.accessToken$ = this.store$.pipe(loginToken(), first(Boolean)) as Observable<string>;
    this.profile$ = this.store$.pipe(profile(), first(Boolean)) as Observable<Profile>;
  }

  getTrips(): Observable<Trip[]> {
    return this.getShortTrips().pipe(
      switchMap((bookings: ShortTrip[]) => {
        if (bookings.length === 0) {
          return of([]);
        }
        return combineLatest(
          bookings.map((booking: ShortTrip) =>
            this.getFullBooking(booking.recLoc, booking.lastName).pipe(catchError(() => of(null)))
          )
        ).pipe(map((values) => values.filter(Boolean)));
      })
    );
  }

  getShortTrips(): Observable<ShortTrip[]> {
    const apiUrl = `${this.configService.cfg.bookingServiceUrl}/member/listbookings/short`;
    const keyId = this.configService.cfg.bookingServiceApiKey;

    return this.accessToken$.pipe(
      switchMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            // eslint-disable-next-line camelcase
            oauth_token: token,
            'x-api-key': keyId,
            // eslint-disable-next-line sonarjs/no-duplicate-string
            'Content-Type': 'application/json',
          }),
        };
        return this.http.get<{ bookings: ShortTrip[] }>(apiUrl, httpOptions).pipe(
          map((response) => {
            if (response?.bookings) {
              return response.bookings;
            }
            return [];
          }),
          catchError((err) => {
            this.sentryLogger.warn(err.message, { error: err });
            return throwError(() => err);
          })
        );
      })
    );
  }

  getFullBooking(recLoc: string, lastName: string): Observable<Trip> {
    const apiUrl = `${this.configService.cfg.bookingServiceUrl}/member/booking/${recLoc}/${lastName}?excludeBookingDetails=true`;
    const keyId = this.configService.cfg.bookingServiceApiKey;

    return this.accessToken$.pipe(
      switchMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            // eslint-disable-next-line camelcase
            oauth_token: token,
            'x-api-key': keyId,
            'Content-Type': 'application/json',
          }),
        };
        return this.http.get<Trip>(apiUrl, httpOptions);
      })
    );
  }

  addTrip(bookingReference: string): Observable<AddFrequentFlyerResponse> {
    return combineLatest([this.accessToken$, this.profile$]).pipe(
      switchMap(([token, memberProfile]: [string, Profile]) => {
        const apiUrl = `${this.configService.cfg.bookingServiceUrl}/booking/${bookingReference}/${memberProfile.lastname}/addFrequentFlyer`;
        const keyId = this.configService.cfg.bookingServiceApiKey;

        const httpOptions = {
          headers: new HttpHeaders({
            // eslint-disable-next-line camelcase
            oauth_token: token,
            'X-Api-Key': keyId,
            'Content-Type': 'application/json',
          }),
        };

        const params = {
          firstName: memberProfile.firstname,
          frequentFlyerNumber: memberProfile.memberNumber,
        };
        return this.http.post<AddFrequentFlyerResponse>(apiUrl, params, httpOptions);
      }),
      map((response) => {
        if (response.status.message === 'OK') {
          return response;
        }
        throw new Error('Error when adding booking');
      }),
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  verifyAddedTrip(bookingReference: string): Observable<boolean> {
    const MAX_ATTEMPTS = 200;
    const BACKOFF_MS = 1000;
    return this.getShortTrips().pipe(
      map((trips: ShortTrip[]): boolean => trips.some((trip) => trip.recLoc === bookingReference)),
      tap((tripFound: boolean) => {
        if (tripFound) {
          this.store$.dispatch(TripsActions.fetchTrips());
        }
      }),
      repeatWhen((attempts) =>
        attempts.pipe(
          map((_, i) => {
            if (i >= MAX_ATTEMPTS) {
              throw new Error('Max verification attempts reached');
            }
            return timer(BACKOFF_MS);
          })
        )
      ),
      takeWhile((tripFound: boolean) => !tripFound, true)
    );
  }
}
